/**
 * 📏 RINTINTIN DESIGN SYSTEM - SPACING TOKENS
 * ===========================================
 * 
 * Système d'espacement cohérent basé sur une grille de 4px
 * Suit les principes de design harmonieux et prévisibles
 */

// ==========================================
// UNITÉ DE BASE - Compatible Ant Design
// ==========================================
const baseUnit = 4; // 4px - unité de référence (même qu'Ant Design)

// ==========================================
// ESPACEMENTS - ÉCHELLE GÉOMÉTRIQUE
// ==========================================
export const spacing = {
  // Espacements micro (0-8px)
  0: '0',                    // 0px
  px: '1px',                 // 1px (bordures fines)
  0.5: `${baseUnit * 0.5}px`, // 2px
  1: `${baseUnit * 1}px`,     // 4px
  2: `${baseUnit * 2}px`,     // 8px

  // Espacements petits (12-24px)
  3: `${baseUnit * 3}px`,     // 12px
  4: `${baseUnit * 4}px`,     // 16px
  5: `${baseUnit * 5}px`,     // 20px
  6: `${baseUnit * 6}px`,     // 24px

  // Espacements moyens (28-48px)
  7: `${baseUnit * 7}px`,     // 28px
  8: `${baseUnit * 8}px`,     // 32px
  9: `${baseUnit * 9}px`,     // 36px
  10: `${baseUnit * 10}px`,   // 40px
  11: `${baseUnit * 11}px`,   // 44px
  12: `${baseUnit * 12}px`,   // 48px

  // Espacements larges (52-96px)
  14: `${baseUnit * 14}px`,   // 56px
  16: `${baseUnit * 16}px`,   // 64px
  20: `${baseUnit * 20}px`,   // 80px
  24: `${baseUnit * 24}px`,   // 96px

  // Espacements extra-larges (112px+)
  28: `${baseUnit * 28}px`,   // 112px
  32: `${baseUnit * 32}px`,   // 128px
  36: `${baseUnit * 36}px`,   // 144px
  40: `${baseUnit * 40}px`,   // 160px
  44: `${baseUnit * 44}px`,   // 176px
  48: `${baseUnit * 48}px`,   // 192px
  52: `${baseUnit * 52}px`,   // 208px
  56: `${baseUnit * 56}px`,   // 224px
  60: `${baseUnit * 60}px`,   // 240px
  64: `${baseUnit * 64}px`,   // 256px
  72: `${baseUnit * 72}px`,   // 288px
  80: `${baseUnit * 80}px`,   // 320px
  96: `${baseUnit * 96}px`,   // 384px
} as const;

// ==========================================
// ALIASES SÉMANTIQUES
// ==========================================
export const spacingAliases = {
  // Espacements de composants
  componentPaddingTiny: spacing[1],      // 4px
  componentPaddingSmall: spacing[2],     // 8px
  componentPaddingMedium: spacing[4],    // 16px
  componentPaddingLarge: spacing[6],     // 24px
  componentPaddingXLarge: spacing[8],    // 32px

  // Espacements entre éléments
  elementGapTiny: spacing[1],            // 4px
  elementGapSmall: spacing[2],           // 8px
  elementGapMedium: spacing[4],          // 16px
  elementGapLarge: spacing[6],           // 24px
  elementGapXLarge: spacing[8],          // 32px

  // Espacements de sections
  sectionGapSmall: spacing[8],           // 32px
  sectionGapMedium: spacing[12],         // 48px
  sectionGapLarge: spacing[16],          // 64px
  sectionGapXLarge: spacing[24],         // 96px

  // Espacements de layout
  layoutMarginSmall: spacing[4],         // 16px
  layoutMarginMedium: spacing[8],        // 32px
  layoutMarginLarge: spacing[12],        // 48px
  layoutMarginXLarge: spacing[20],       // 80px

  // Espacements de conteneurs
  containerPaddingSmall: spacing[4],     // 16px
  containerPaddingMedium: spacing[6],    // 24px
  containerPaddingLarge: spacing[8],     // 32px
  containerPaddingXLarge: spacing[12],   // 48px

  // Espacements de grilles
  gridGapSmall: spacing[4],              // 16px
  gridGapMedium: spacing[6],             // 24px
  gridGapLarge: spacing[8],              // 32px

  // Espacements spécialisés
  iconGap: spacing[2],                   // 8px - entre icône et texte
  buttonPadding: spacing[4],             // 16px - padding horizontal boutons
  inputPadding: spacing[3],              // 12px - padding inputs
  cardPadding: spacing[6],               // 24px - padding cartes
  modalPadding: spacing[8],              // 32px - padding modales
} as const;

// ==========================================
// RAYONS DE BORDURE
// ==========================================
export const borderRadius = {
  none: '0',
  sm: '0.125rem',    // 2px
  base: '0.25rem',   // 4px - défaut
  md: '0.375rem',    // 6px
  lg: '0.5rem',      // 8px
  xl: '0.75rem',     // 12px
  '2xl': '1rem',     // 16px
  '3xl': '1.5rem',   // 24px
  full: '9999px',    // Cercle parfait
} as const;

// ==========================================
// TAILLES DE BORDURES
// ==========================================
export const borderWidth = {
  0: '0',
  DEFAULT: '1px',    // Défaut
  2: '2px',
  4: '4px',
  8: '8px',
} as const;

// ==========================================
// OMBRES
// ==========================================
export const shadows = {
  none: 'none',
  
  // Ombres subtiles
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  DEFAULT: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  
  // Ombres moyennes
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  
  // Ombres fortes
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  
  // Ombres intérieures
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  
  // Ombres colorées pour focus/états
  focusPrimary: '0 0 0 3px rgba(127, 176, 105, 0.2)',
  focusError: '0 0 0 3px rgba(245, 101, 101, 0.2)',
  focusWarning: '0 0 0 3px rgba(246, 198, 116, 0.2)',
} as const;

// ==========================================
// TAILLES D'ÉLÉMENTS COMMUNS
// ==========================================
export const sizes = {
  // Hauteurs d'éléments de formulaire (compatibles Ant Design)
  inputHeight: {
    sm: '24px',        // Ant Design small
    md: '32px',        // Ant Design default
    lg: '40px',        // Ant Design large
    xl: '48px',        // Extension
  },
  
  // Hauteurs de boutons (compatibles Ant Design)
  buttonHeight: {
    sm: '24px',        // Ant Design small
    md: '32px',        // Ant Design default  
    lg: '40px',        // Ant Design large
    xl: '48px',        // Extension
  },
  
  // Tailles d'icônes courantes
  iconSize: {
    xs: spacing[3],    // 12px
    sm: spacing[4],    // 16px
    md: spacing[5],    // 20px
    lg: spacing[6],    // 24px
    xl: spacing[8],    // 32px
    '2xl': spacing[12], // 48px
  },
  
  // Largeurs de conteneurs
  container: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
} as const;

// ==========================================
// TYPES POUR TYPESCRIPT
// ==========================================
export type SpacingScale = keyof typeof spacing;
export type BorderRadius = keyof typeof borderRadius;
export type Shadow = keyof typeof shadows;
export type SpacingAlias = keyof typeof spacingAliases;

// ==========================================
// HELPERS POUR UTILISATION
// ==========================================
export const getSpacing = (scale: SpacingScale) => {
  return spacing[scale];
};

export const getSpacingAlias = (alias: SpacingAlias) => {
  return spacingAliases[alias];
};

// ==========================================
// UTILITAIRES CSS-IN-JS
// ==========================================
export const spacingUtils = {
  // Padding uniforme
  p: (scale: SpacingScale) => ({ padding: spacing[scale] }),
  px: (scale: SpacingScale) => ({ 
    paddingLeft: spacing[scale], 
    paddingRight: spacing[scale] 
  }),
  py: (scale: SpacingScale) => ({ 
    paddingTop: spacing[scale], 
    paddingBottom: spacing[scale] 
  }),
  
  // Margin uniforme
  m: (scale: SpacingScale) => ({ margin: spacing[scale] }),
  mx: (scale: SpacingScale) => ({ 
    marginLeft: spacing[scale], 
    marginRight: spacing[scale] 
  }),
  my: (scale: SpacingScale) => ({ 
    marginTop: spacing[scale], 
    marginBottom: spacing[scale] 
  }),
  
  // Gap pour flexbox/grid
  gap: (scale: SpacingScale) => ({ gap: spacing[scale] }),
} as const;
